/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: API Architecture
 * PURPOSE: User profile and settings management including preferences, notifications, and permissions
 *
 * NEW PATTERNS IMPLEMENTED:
 * - ApiClient Pattern: Centralized HTTP handler with generic types
 * - Error Handling: Comprehensive ApiError parsing with field-level errors
 * - Token Refresh: Automatic retry on 401 with exponential backoff
 * - Safe Variants: Tuple return [data | null, error | null]
 * - Path Builder: Functional endpoint construction via API_CONFIG
 *
 * KEY FEATURES:
 * - User profile retrieval and updates
 * - User preferences management
 * - Notification settings configuration
 * - UI settings persistence
 * - Password change operations
 * - Permission refresh with state synchronization
 * - Global permission refresh utility
 *
 * RELATED FILES:
 * - src/lib/services/api/client.ts
 * - src/lib/config/apiConfig.ts
 * - src/lib/state/auth.state.svelte.ts
 * - src/lib/types/core/user.types.ts
 */

// src/services/api/profile.api.ts
import { apiClient } from './client';
import { API_CONFIG } from '$lib/config/apiConfig';
import { authState, updateUserProfile } from '$lib/state/auth.state.svelte';
import { eventBus } from '$lib/core/services/EventBusService';
import { devLog } from '../../utils/dev';
import type {
	UserProfile,
	UserPreferences,
	NotificationSettings,
	UISettings,
	User
} from '$lib/types/core/user.types';
import type { ProfileUpdateRequest } from '$lib/types/api/requests.types';
import type { ProfileUpdateResponse } from '$lib/types/api/responses.types';

/**
 * Profile API service for user profile operations
 */
export class ProfileApi {
	/**
	 * Get current user profile including preferences
	 * @returns Promise with the user profile
	 */
	async getCurrentProfile(): Promise<UserProfile> {
		try {
			const response = await apiClient.get<ProfileUpdateResponse>(API_CONFIG.USERS.ME());

			// Update the user state with the profile data
			this.updateUserState(response);

			return response as UserProfile;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update user profile information
	 * @param data Profile data to update
	 * @returns Promise with the updated user
	 */
	async updateProfile(data: ProfileUpdateRequest): Promise<ProfileUpdateResponse> {
		try {
			// PUT request with fields to update
			const response = await apiClient.put<ProfileUpdateResponse>(
				API_CONFIG.USERS.UPDATE_PROFILE(),
				data
			);

			// Update the user state
			this.updateUserState(response);

			return response;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get user settings (all types)
	 * @returns Promise with all user settings
	 */
	async getSettings(): Promise<{
		preferences: UserPreferences;
		notification_settings: NotificationSettings;
		ui_settings: UISettings;
	}> {
		try {
			const response = await apiClient.get<{
				preferences: UserPreferences;
				notification_settings: NotificationSettings;
				ui_settings: UISettings;
			}>(API_CONFIG.USERS.SETTINGS());

			// Update the user state with settings
			if (authState.user) {
				updateUserProfile({
					preferences: response.preferences,
					notification_settings: response.notification_settings,
					ui_settings: response.ui_settings
				});
			}

			return response;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update UI settings
	 * @param settings UI settings to update
	 * @returns Promise with updated UI settings
	 */
	async updateUISettings(settings: Partial<UISettings>): Promise<UISettings> {
		try {
			const response = await apiClient.put<UISettings>(API_CONFIG.USERS.SETTINGS_UI(), settings);

			// Update the user state
			if (authState.user) {
				updateUserProfile({
					ui_settings: response
				});
			}

			return response;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update user preferences
	 * @param preferences Preferences to update
	 * @returns Promise with updated preferences
	 */
	async updatePreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
		try {
			const response = await apiClient.put<UserPreferences>(
				API_CONFIG.USERS.SETTINGS_PREFERENCES(),
				preferences
			);

			// Update the user state
			if (authState.user) {
				updateUserProfile({
					preferences: response
				});
			}

			return response;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update user password
	 * @param data Password update data
	 * @returns Promise with success response
	 */
	async updatePassword(data: {
		current_password: string;
		new_password: string;
		confirm_password?: string;
	}): Promise<{ message: string }> {
		try {
			const response = await apiClient.put<{ message: string }>(
				API_CONFIG.USERS.PASSWORD(),
				data
			);
			return response;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Update notification settings
	 * @param settings Notification settings to update
	 * @returns Promise with updated notification settings
	 */
	async updateNotificationSettings(
		settings: Partial<NotificationSettings>
	): Promise<NotificationSettings> {
		try {
			const response = await apiClient.put<NotificationSettings>(
				API_CONFIG.USERS.SETTINGS_NOTIFICATIONS(),
				settings
			);

			// Update the user state
			if (authState.user) {
				updateUserProfile({
					notification_settings: response
				});
			}

			return response;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Get fresh permissions from the backend
	 * This bypasses any cached data and forces a database read
	 * @returns Promise with the fresh permissions
	 */
	async refreshPermissions(): Promise<{
		direct_permissions: Array<{ resource: string; actions: string[]; scope?: string }>;
	}> {
		try {
			const response = await apiClient.get<{
				direct_permissions: Array<{ resource: string; actions: string[]; scope?: string }>;
			}>(API_CONFIG.USERS.PERMISSIONS());

			// Update the auth state with fresh permissions
			if (response && response.direct_permissions) {
				// Import the auth state update function
				const { setFreshPermissions } = await import('$lib/state/auth.state.svelte');
				setFreshPermissions(response);
				devLog('ProfileApi: Refreshed permissions from backend', {
					permissionCount: response.direct_permissions.length,
					permissions: response.direct_permissions
				});
			}

			return response;
		} catch (error) {
			throw error;
		}
	}

	/**
	 * Helper method to update the authStateSvelte with profile data
	 */
	private updateUserState(profileData: ProfileUpdateResponse): void {
		// Skip if no user in state
		if (!authState.user) {
			devLog('ProfileApi: No user in state to update');
			return;
		}

		// Cast the current user to UserProfile
		const userProfile = authState.user as unknown as UserProfile;

		// Create an updated profile (avoid direct nested property mutation)
		const updatedProfile: UserProfile = {
			...userProfile
		};

		// Update basic user fields if present
		if (profileData.name) updatedProfile.name = profileData.name;
		if (profileData.email) updatedProfile.email = profileData.email;

		if (profileData.organization_name)
			updatedProfile.organization_name = profileData.organization_name;
		if (profileData.organization_code)
			updatedProfile.organization_code = profileData.organization_code;

		// Update preferences if present (handle null)
		if (profileData.preferences !== undefined && profileData.preferences !== null) {
			updatedProfile.preferences = {
				...(userProfile.preferences || {}),
				...profileData.preferences
			} as UserPreferences;
		}

		// Update notification settings if present (handle null)
		if (
			profileData.notification_settings !== undefined &&
			profileData.notification_settings !== null
		) {
			updatedProfile.notification_settings = {
				...(userProfile.notification_settings || {}),
				...profileData.notification_settings
			} as NotificationSettings;
		}

		// Update UI settings if present (handle null)
		if (profileData.ui_settings !== undefined && profileData.ui_settings !== null) {
			updatedProfile.ui_settings = {
				...(userProfile.ui_settings || {}),
				...profileData.ui_settings
			} as UISettings;
		}

		// Use updateUserProfile which handles localStorage and events
		updateUserProfile(updatedProfile as Partial<UserProfile>);
		devLog('ProfileApi: User state updated via updateUserProfile');
	}
}

// Create a singleton instance
export const profileApi = new ProfileApi();

// Export the default instance
export default profileApi;

// Add a global function for easy permission refresh from console
if (typeof window !== 'undefined') {
	(window as any).refreshPermissions = async () => {
		try {
			const result = await profileApi.refreshPermissions();
			// Check for ALERT_REVERSION specifically
			const alertPerms = result.direct_permissions.filter(
				(p) => p.resource === 'ALERT_REVERSION' || p.resource === 'ALERT' || p.resource === 'OPA'
			);
			return result;
		} catch (error) {
			throw error;
		}
	};
}
